import { useMemo } from "react";
import { ArrowLeft, Building2, Package, Calendar, HandCoins, Wallet } from "lucide-react";
import Button from "../ui/Button";
import MetricCard from "../ui/MetricCard";
import { Table, THead, TR, TH, TD, EmptyState } from "../ui/Table";
import {
  aggregateDepartmentMonth,
  aggregateOfferMonth,
  computeDepartmentMRR,
  computeDepartmentCashCollected,
  computeDepartmentNewSales,
  computeDepartmentCashInBank,
  computeDepartmentBudgetHeld,
  computeDepartmentDividends,
  computeDepartmentStripeFees,
  computeNextInvoicesDue,
} from "../../lib/calculations";
import { formatUSD } from "../../lib/formatters";
import { today, monthKey, formatDate, toISO } from "../../lib/dateUtils";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Build a human-friendly label for the active filter window.
// Examples: "YTD" (Jan→current, current year), "2025" (full past year),
// "April 2026" (single month), "Jan–Apr 2026" (multi-month range).
function periodLabel(fromMonth, toMonth, year, currentYear, currentMonthIdx) {
  const isYtdDefault =
    year === currentYear && fromMonth === 1 && toMonth - 1 >= currentMonthIdx;
  if (isYtdDefault) return "YTD";
  if (fromMonth === 1 && toMonth === 12) return `${year}`;
  if (fromMonth === toMonth) return `${MONTH_LABELS[fromMonth - 1]} ${year}`;
  return `${MONTH_LABELS[fromMonth - 1]}–${MONTH_LABELS[toMonth - 1]} ${year}`;
}

// Drill-down view for one department. Shows:
//   - 4 YTD KPIs (revenue, gross, net, partner-cut-adjusted)
//   - Offers in this department with per-offer YTD P&L
//   - Partner rules scoped to this department
//   - Client budget held for this department's offers
export default function DepartmentProfile({ department, displayName, state, dateFilter, onBack }) {
  const offers = state.offers || [];
  const team = state.team || [];
  const partnerRules = state.partnerRules || [];
  const payments = state.payments || [];

  const currentYear = today().getFullYear();
  const year = dateFilter?.year && dateFilter.year !== "all" ? dateFilter.year : currentYear;
  const currentMonthIdx = today().getMonth();
  const cutoffMonthIdx = year === currentYear ? currentMonthIdx : 11;

  // Month-range filter from the global date picker. Defaults to full year
  // (Jan→Dec). For the current year we never look past the current month —
  // future months have no data and would just zero out.
  const fromMonth = dateFilter?.fromMonth || 1;
  const toMonth = dateFilter?.toMonth || 12;
  const startIdx = Math.max(0, fromMonth - 1);
  const endIdx = Math.min(toMonth - 1, cutoffMonthIdx);
  const period = periodLabel(fromMonth, toMonth, year, currentYear, currentMonthIdx);

  // Offers in this dept (or null-dept for "Unassigned").
  const deptOffers = useMemo(
    () => offers.filter((o) => (department == null ? !o.department : o.department === department)),
    [offers, department]
  );

  // Aggregate the whole department over the selected month range. Track
  // team vs tools separately for COGS / OPEX / Marketing so the waterfall
  // can show Payroll, Marketing and Tools as separate lines.
  const ytd = useMemo(() => {
    let revenue = 0, partner = 0;
    let cogsTeam = 0, cogsTools = 0;
    let opexTeam = 0, opexTools = 0;
    let marketingTeam = 0, marketingTools = 0;
    for (let i = startIdx; i <= endIdx; i++) {
      const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
      const m = aggregateDepartmentMonth(department, state, mKey);
      revenue += m.revenue;
      cogsTeam += m.cogs.team || 0;
      cogsTools += m.cogs.tools || 0;
      opexTeam += m.opex.team || 0;
      opexTools += m.opex.tools || 0;
      marketingTeam += m.marketing?.team || 0;
      marketingTools += m.marketing?.tools || 0;
      partner += m.partnerCutsOwed;
    }
    const cogsTotal = cogsTeam + cogsTools;
    const opexTotal = opexTeam + opexTools;
    const marketingTotal = marketingTeam + marketingTools;
    // Payroll combines team across ALL buckets so it's "all team salary".
    const payrollTotal = cogsTeam + opexTeam + marketingTeam;
    const toolsCogs = cogsTools;
    const toolsOpex = opexTools;
    return {
      revenue, cogsTotal, opexTotal, marketingTotal, partner,
      cogsTeam, cogsTools, opexTeam, opexTools, marketingTeam, marketingTools,
      payrollTotal, toolsCogs, toolsOpex,
      gross: revenue - cogsTotal,
      net: revenue - cogsTotal - opexTotal - marketingTotal,
      netAfterPartner: revenue - cogsTotal - opexTotal - marketingTotal - partner,
    };
  }, [department, state, year, startIdx, endIdx]);

  // Cash collected for this department, restricted to the selected month range.
  const { cashCollected, remainingToCollect } = useMemo(() => {
    let cash = 0;
    let outstanding = 0;
    for (const p of payments) {
      const offerId = p.offerId || state.clients.find((c) => c.id === p.clientId)?.primaryOfferId;
      const offer = offers.find((o) => o.id === offerId);
      if (!offer) continue;
      const inDept = department == null ? !offer.department : offer.department === department;
      if (!inDept) continue;
      const ref = p.paidDate || p.bookedDate;
      if (!ref) continue;
      const mk = monthKey(ref);
      if (!mk || !mk.startsWith(`${year}-`)) continue;
      const monthIdx = Number(mk.slice(5, 7)) - 1;
      if (monthIdx < startIdx || monthIdx > endIdx) continue;
      if (p.status === "paid") cash += Number(p.amount) || 0;
      else if (p.status === "pending" || p.status === "scheduled") outstanding += Number(p.amount) || 0;
    }
    return { cashCollected: cash, remainingToCollect: outstanding };
  }, [payments, offers, state.clients, department, year, startIdx, endIdx]);

  // New "accounting-software" KPIs — MRR, new sales, cash in bank, budgets held,
  // dividends, next invoices due.
  const currentMonthMKey = `${currentYear}-${String(currentMonthIdx + 1).padStart(2, "0")}`;
  const currentMRR = useMemo(
    () => computeDepartmentMRR(department, state, currentMonthMKey),
    [department, state, currentMonthMKey]
  );

  // "New sales" summed across the active range (cash collected − recurring MRR per month).
  const newSalesYtd = useMemo(() => {
    let total = 0;
    for (let i = startIdx; i <= endIdx; i++) {
      const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
      total += computeDepartmentNewSales(department, state, mKey);
    }
    return total;
  }, [department, state, year, startIdx, endIdx]);

  const cashInBank = useMemo(
    () => computeDepartmentCashInBank(department, state, toISO(today())),
    [department, state]
  );

  const budgetsHeld = useMemo(
    () => computeDepartmentBudgetHeld(department, state, currentMonthMKey),
    [department, state, currentMonthMKey]
  );

  const dividendsYtd = useMemo(() => {
    const fromDate = `${year}-${String(fromMonth).padStart(2, "0")}-01`;
    const toDate = `${year}-${String(toMonth).padStart(2, "0")}-31`;
    return computeDepartmentDividends(department, state, fromDate, toDate);
  }, [department, state, year, fromMonth, toMonth]);

  const nextInvoices = useMemo(
    () => computeNextInvoicesDue(department, state, 5),
    [department, state]
  );


  // Stripe processor fees for the active range — subtracted from gross
  // revenue to produce "Net Revenue" (what actually deposits to the bank).
  // Then propagated through gross profit / net profit / remaining.
  const stripeFeesYtd = useMemo(() => {
    const fromDate = `${year}-${String(fromMonth).padStart(2, "0")}-01`;
    const toDate = `${year}-${String(toMonth).padStart(2, "0")}-31`;
    return computeDepartmentStripeFees(department, state, fromDate, toDate);
  }, [department, state, year, fromMonth, toMonth]);

  const netRevenue = ytd.revenue - stripeFeesYtd;
  const grossAfterFees = netRevenue - ytd.cogsTotal;
  const netAfterFees = grossAfterFees - ytd.opexTotal - ytd.marketingTotal;
  const remainingAfterFees = netAfterFees - ytd.partner;

  // "Money in Bank" = Net Profit for the period. Same number as the
  // = Net profit line in the P&L waterfall below: revenue minus Stripe fees,
  // minus all tools (delivery + overhead), minus all team payroll. So the
  // card and waterfall always agree without any manual department tagging.
  const moneyInBank = netAfterFees;

  // Per-offer breakdown over the selected month range (sorted by revenue desc).
  const offerRows = useMemo(() => {
    return deptOffers.map((offer) => {
      let revenue = 0, cogsTotal = 0, opexTotal = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
        const m = aggregateOfferMonth(offer.id, state, mKey);
        revenue += m.revenue;
        cogsTotal += m.cogs.total;
        opexTotal += m.opex.total;
      }
      return {
        offer,
        revenue,
        cogsTotal,
        opexTotal,
        gross: revenue - cogsTotal,
        net: revenue - cogsTotal - opexTotal,
      };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [deptOffers, state, year, startIdx, endIdx]);

  // Partner rules scoped here.
  const deptPartnerRules = useMemo(() => {
    return partnerRules.filter((r) =>
      (r.scope === "department" && r.scopeId === department) ||
      (r.scope === "offer" && deptOffers.some((o) => o.id === r.scopeId))
    );
  }, [partnerRules, department, deptOffers]);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Departments
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Building2 size={18} className="text-brand-blue" />
        <h2 className="font-display font-semibold text-xl text-brand-ink dark:text-[var(--text-primary)]">{displayName}</h2>
        <span className="text-xs text-brand-slate">· {deptOffers.length} offer{deptOffers.length === 1 ? "" : "s"} · {year}</span>
      </div>

      {/* Single Money in Bank card — net cash flow for the period:
          cash collected (net of Stripe fees) minus expenses paid. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MetricCard
          label={`Money in Bank · ${period}`}
          value={formatUSD(moneyInBank)}
          accent={moneyInBank > 0 ? "green" : moneyInBank < 0 ? "red" : "default"}
          sublabel="net profit (revenue − fees − tools − payroll)"
        />
      </div>

      {/* NEXT INVOICES DUE */}
      {nextInvoices.length > 0 && (
        <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)] flex items-center gap-2">
            <Calendar size={14} className="text-brand-slate" />
            <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">
              Next invoices due
            </div>
          </div>
          <Table>
            <THead>
              <TR>
                <TH>Due</TH>
                <TH>Client</TH>
                <TH>Offer</TH>
                <TH>Note</TH>
                <TH className="text-right">Amount</TH>
              </TR>
            </THead>
            <tbody>
              {nextInvoices.map((inv) => {
                const days = Math.max(0, Math.round((new Date(inv.dueDate) - new Date(toISO(today()))) / 86400000));
                return (
                  <TR key={inv.id}>
                    <TD className="text-xs">
                      {formatDate(inv.dueDate)}
                      <span className="text-brand-slate ml-1.5">· {days === 0 ? "today" : `in ${days}d`}</span>
                    </TD>
                    <TD className="font-medium">{inv.clientName}</TD>
                    <TD className="text-xs text-brand-slate">{inv.offerName}</TD>
                    <TD className="text-xs text-brand-slate truncate max-w-[260px]">{inv.note || "—"}</TD>
                    <TD className="text-right font-semibold">{formatUSD(inv.amount)}</TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* P&L waterfall */}
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)]">
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">P&amp;L waterfall · {period}</div>
        </div>
        <table className="w-full text-sm">
          <tbody>
            <WaterfallRow label="Revenue (gross)" value={ytd.revenue} sign="+" base={ytd.revenue} remaining={ytd.revenue} />
            <WaterfallRow label="Stripe fees" value={-stripeFeesYtd} sign="−" muted accent="purple" base={ytd.revenue} remaining={netRevenue} />
            <WaterfallRow label="= Net Revenue" value={netRevenue} bold base={ytd.revenue} remaining={netRevenue} />
            <WaterfallRow label="Tools (delivery)" value={-ytd.toolsCogs} sign="−" muted base={ytd.revenue} remaining={netRevenue - ytd.toolsCogs} />
            <WaterfallRow label="= Gross profit" value={grossAfterFees} bold base={ytd.revenue} remaining={grossAfterFees} />
            <WaterfallRow label="Payroll" value={-ytd.payrollTotal} sign="−" muted base={ytd.revenue} remaining={grossAfterFees - ytd.payrollTotal} />
            <WaterfallRow label="Marketing" value={-ytd.marketingTotal} sign="−" muted base={ytd.revenue} remaining={grossAfterFees - ytd.payrollTotal - ytd.marketingTotal} />
            <WaterfallRow label="Tools (overhead)" value={-ytd.toolsOpex} sign="−" muted base={ytd.revenue} remaining={netAfterFees} />
            <WaterfallRow label="= Net profit" value={netAfterFees} bold base={ytd.revenue} remaining={netAfterFees} />
            <WaterfallRow label="Partner cuts owed" value={-ytd.partner} sign="−" muted accent="purple" base={ytd.revenue} remaining={remainingAfterFees} />
            <WaterfallRow label="= Remaining for owner" value={remainingAfterFees} bold accent={remainingAfterFees > 0 ? "green" : "red"} base={ytd.revenue} remaining={remainingAfterFees} />
          </tbody>
        </table>
      </div>

      {/* Offers under this department */}
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)] flex items-center gap-2">
          <Package size={14} className="text-brand-slate" />
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">Offers · {period}</div>
        </div>
        {offerRows.length === 0 ? (
          <EmptyState icon={Package} title="No offers in this department" message="Tag an offer with this department in the Offers tab to see it here." />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Offer</TH>
                <TH className="text-right">Revenue</TH>
              </TR>
            </THead>
            <tbody>
              {offerRows.map(({ offer, revenue }) => (
                <TR key={offer.id}>
                  <TD className="font-medium">{offer.name}</TD>
                  <TD className="text-right font-semibold">{formatUSD(revenue)}</TD>
                </TR>
              ))}
              <TR className="bg-gray-50 dark:bg-[var(--bg-soft)] border-t-2 border-gray-200 dark:border-[var(--border-soft)]">
                <TD className="font-semibold text-brand-slate uppercase tracking-wide text-[10px]">Total · {period}</TD>
                <TD className="text-right font-bold">
                  {formatUSD(offerRows.reduce((s, r) => s + (r.revenue || 0), 0))}
                </TD>
              </TR>
            </tbody>
          </Table>
        )}
      </div>

      {/* Partner rules scoped to this department */}
      {deptPartnerRules.length > 0 && (
        <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)]">
            <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">Partner cuts on this department</div>
          </div>
          <Table>
            <THead>
              <TR>
                <TH>Partner</TH>
                <TH>Scope</TH>
                <TH>Basis</TH>
                <TH className="text-right">%</TH>
              </TR>
            </THead>
            <tbody>
              {deptPartnerRules.map((r) => {
                const partner = team.find((m) => m.id === r.partnerId);
                const scopeLabel = r.scope === "department" ? `Dept: ${r.scopeId}` : `Offer: ${offers.find((o) => o.id === r.scopeId)?.name || r.scopeId}`;
                return (
                  <TR key={r.id}>
                    <TD className="font-medium">{partner?.name || "—"}</TD>
                    <TD className="text-xs text-brand-slate">{scopeLabel}</TD>
                    <TD className="text-xs text-brand-slate">{r.basis === "net_profit" ? "% of net profit" : "% of revenue"}</TD>
                    <TD className="text-right text-xs font-semibold">{Number(r.percent) || 0}%</TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* Per-month P&L matrix for this department — 3 rows:
          Revenue, Gross Profit, Net Profit. */}
      <div className="bg-white dark:bg-[var(--bg-surface)] rounded-xl border border-gray-100 dark:border-[var(--border-soft)] shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-[var(--border-soft)]">
          <div className="font-display font-semibold text-base text-brand-ink dark:text-[var(--text-primary)]">Monthly P&L · {year}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-[var(--bg-soft)]">
              <tr>
                <th className="px-3 py-2 text-left font-medium uppercase tracking-wide text-[10px] text-brand-slate border-r border-gray-100 dark:border-[var(--border-soft)]">Metric</th>
                {MONTH_LABELS.map((m, i) => {
                  const inRange = i >= startIdx && i <= toMonth - 1;
                  return (
                    <th key={m} className={`px-3 py-2 text-center font-medium uppercase tracking-wide text-[10px] ${inRange ? "text-brand-slate" : "text-gray-300"}`}>{m}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Pre-compute each month's metrics once. OPEX in this view
                // bundles tool overhead + payroll OPEX + marketing so the
                // row reads as total operating cost.
                const monthly = MONTH_LABELS.map((_, i) => {
                  const isFuture = i > cutoffMonthIdx;
                  if (isFuture) return null;
                  const mKey = `${year}-${String(i + 1).padStart(2, "0")}`;
                  const m = aggregateDepartmentMonth(department, state, mKey);
                  const cogsTotal = m.cogs.total;
                  const opexTotal = m.opex.total + (m.marketing?.total || 0);
                  return {
                    revenue: m.revenue,
                    cogs: cogsTotal,
                    opex: opexTotal,
                    net: m.revenue - cogsTotal - opexTotal,
                  };
                });
                const renderRow = (label, key, opts = {}) => (
                  <tr className="border-t border-gray-100 dark:border-[var(--border-soft)]">
                    <td className={`px-3 py-2 ${opts.bold ? "font-semibold" : ""} text-brand-ink dark:text-[var(--text-primary)] border-r border-gray-100 dark:border-[var(--border-soft)]`}>
                      {label}
                    </td>
                    {MONTH_LABELS.map((_, i) => {
                      const inRange = i >= startIdx && i <= toMonth - 1;
                      const isFuture = i > cutoffMonthIdx;
                      const value = monthly[i]?.[key] || 0;
                      const colorize = opts.color || "default";
                      let color;
                      if (!inRange || isFuture || Math.abs(value) < 0.5) {
                        color = "text-gray-300";
                      } else if (colorize === "cost") {
                        color = "text-brand-slate";
                      } else if (colorize === "profit") {
                        color = value > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600";
                      } else {
                        color = "text-brand-ink dark:text-[var(--text-primary)]";
                      }
                      const displayValue = isFuture || Math.abs(value) < 0.5
                        ? "—"
                        : (colorize === "cost" ? `−${formatUSD(value)}` : formatUSD(value));
                      return (
                        <td key={i} className={`px-3 py-2 text-center ${opts.bold ? "font-semibold" : ""} ${color}`}>
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                );
                return (
                  <>
                    {renderRow("Revenue", "revenue")}
                    {renderRow("COGS", "cogs", { color: "cost" })}
                    {renderRow("OPEX", "opex", { color: "cost" })}
                    {renderRow("Net Profit", "net", { bold: true, color: "profit" })}
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function WaterfallRow({ label, value, sign, bold, muted, accent, base, remaining }) {
  const accentClass =
    accent === "green" ? "text-emerald-700 dark:text-emerald-300" :
    accent === "red" ? "text-red-600 dark:text-red-400" :
    accent === "purple" ? "text-purple-700 dark:text-purple-300" :
    muted ? "text-brand-slate" : "";
  // Deduction rows (sign === "−") show the deduction rate (e.g. Stripe fees
  // 4.1% of revenue, COGS 33% of revenue). Summary rows (Revenue, =Gross, etc.)
  // show cumulative remaining as % of revenue (margin).
  const isDeduction = sign === "−";
  const basis = isDeduction ? Math.abs(value) : (remaining !== undefined ? remaining : value);
  const pct = base && Math.abs(base) > 0.001 ? (basis / base) * 100 : null;
  const pctText = pct === null ? "" : `${pct.toFixed(1)}%`;
  return (
    <tr className="border-t border-gray-100 dark:border-[var(--border-soft)]">
      <td className={`px-4 py-2 ${bold ? "font-semibold" : ""} ${accentClass}`}>
        {sign && <span className="text-brand-slate mr-1.5">{sign}</span>}
        {label}
      </td>
      <td className="px-4 py-2 text-right tabular-nums text-xs text-brand-slate w-20">
        {pctText}
      </td>
      <td className={`px-4 py-2 text-right tabular-nums ${bold ? "font-bold" : ""} ${accentClass}`}>
        {formatUSD(value)}
      </td>
    </tr>
  );
}
